import React from 'react';
import { App, Button, Flex, Typography, theme } from 'antd';
import { CopyOutlined, ExportOutlined, SearchOutlined } from '@ant-design/icons';

import { CustomTooltip } from '~components/Tooltip';

const URL_CELL_STRINGS = {
  copy: 'Copy URL',
  copied: 'URL copied to clipboard',
  copyFailed: 'Could not copy — your browser blocked clipboard access',
  openInNewTab: 'Open in new tab',
  inspect: 'Inspect URL',
};

/** Icon-only control; include the URL so every row doesn't read as "Copy URL". */
function formatUrlActionLabel(action: string, url: string): string {
  return `${action}: ${url}`;
}

/**
 * Row-hover URL actions via CSS (a hovered-row index in React re-renders the whole body).
 * `opacity` keeps the buttons in the tab order; `:focus-within` reveals them. Prefix is
 * `ant5-` (`ConfigProvider prefixCls="ant5"` in `src/index.tsx`). Mounted once by the table.
 */
export const URL_ACTIONS_CSS = `
.search-performance-url-actions {
  opacity: 0;
  transition: opacity 120ms ease-in-out;
}

.ant5-table-row:hover .search-performance-url-actions,
.ant5-table-row:focus-within .search-performance-url-actions,
.search-performance-url-cell:hover .search-performance-url-actions {
  opacity: 1;
}

/* Always visible for anyone who has asked for reduced motion or uses a touch/coarse
   pointer, where "hover" is not a reliable affordance. */
@media (hover: none), (prefers-reduced-motion: reduce) {
  .search-performance-url-actions {
    opacity: 1;
    transition: none;
  }
}
`;

/**
 * `clipboard.writeText` needs a secure context and permission; `execCommand('copy')`
 * covers embedded/automated browsers where it is denied.
 */
async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // Clipboard API denied; try execCommand.
  }

  try {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    // Off-screen but selectable; `display: none` would make selection fail.
    textarea.setAttribute('readonly', '');
    textarea.style.cssText = 'position:fixed;top:-1000px;opacity:0';
    document.body.appendChild(textarea);
    textarea.select();
    const copied = document.execCommand('copy');
    textarea.remove();
    return copied;
  } catch {
    return false;
  }
}

type PagesUrlCellProps = {
  url: string;
  /** Called when the customer selects this URL. Omitted hides the select action. */
  onInspect?: () => void;
  /** Called when the URL is opened in a new tab (the link or the export icon). */
  onUrlClick?: (url: string) => void;
};

function PagesUrlCell({ url, onInspect, onUrlClick }: PagesUrlCellProps) {
  const { token } = theme.useToken();
  const { message } = App.useApp();

  async function handleCopy() {
    if (await copyText(url)) {
      message.success(URL_CELL_STRINGS.copied);
      return;
    }

    message.error(URL_CELL_STRINGS.copyFailed);
  }

  const iconStyle = {
    // FIXME: no exact token — the prototype uses 13px, between fontSizeSM and fontSize.
    fontSize: token.fontSizeSM,
    color: token.colorTextSecondary,
  };

  return (
    <Flex
      className="search-performance-url-cell"
      align="center"
      gap={token.marginXS}
      style={{ minWidth: 0 }}
    >
      {/* CustomTooltip: antd's `ellipsis={{ tooltip }}` would mount a Tooltip per row. */}
      <CustomTooltip placement="top" message={url} wrapperStyle={{ flex: 1, minWidth: 0 }}>
        <Typography.Text ellipsis style={{ display: 'block', fontSize: token.fontSizeSM }}>
          <Typography.Link
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: token.colorPrimary }}
            onClick={() => onUrlClick?.(url)}
          >
            {url}
          </Typography.Link>
        </Typography.Text>
      </CustomTooltip>

      <Flex
        className="search-performance-url-actions"
        component="span"
        align="center"
        // FIXME: no exact token — the prototype tucks the icons 2px apart.
        gap={token.marginXXS}
        style={{ flexShrink: 0 }}
      >
        <CustomTooltip placement="top" message={URL_CELL_STRINGS.copy}>
          <Button
            type="text"
            size="small"
            icon={<CopyOutlined style={iconStyle} />}
            aria-label={formatUrlActionLabel(URL_CELL_STRINGS.copy, url)}
            onClick={handleCopy}
          />
        </CustomTooltip>

        {/* Anchor, not window.open: middle-click / cmd-click keep working. */}
        <CustomTooltip placement="top" message={URL_CELL_STRINGS.openInNewTab}>
          <Button
            type="text"
            size="small"
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            icon={<ExportOutlined style={iconStyle} />}
            aria-label={formatUrlActionLabel(URL_CELL_STRINGS.openInNewTab, url)}
            onClick={() => onUrlClick?.(url)}
          />
        </CustomTooltip>

        {onInspect && (
          <CustomTooltip placement="top" message={URL_CELL_STRINGS.inspect}>
            <Button
              type="text"
              size="small"
              icon={<SearchOutlined style={iconStyle} />}
              aria-label={formatUrlActionLabel(URL_CELL_STRINGS.inspect, url)}
              onClick={onInspect}
            />
          </CustomTooltip>
        )}
      </Flex>
    </Flex>
  );
}

export default PagesUrlCell;
